<template>
  <v-container>
    <template v-if="loading">
      <v-dialog v-model="loading" hide-overlay persistent width="300">
        <v-card color="primary" dark>
          <v-card-text>
            Loading home underwriting information...
            <v-progress-linear indeterminate color="white" class="mb-0" />
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
    <template v-else>
      <v-row wrap>
        <v-col cols="6" class="py-1">
          <DisplayField label="Year Built" :value="homeUnderwriting.yearBuilt" />
        </v-col>
        <v-col cols="6" class="py-1">
          <DisplayField label="Residence Type" :value="residenceType" />
        </v-col>
        <v-col cols="6" class="py-1">
          <DisplayField label="Occupancy Type" :value="occupancyType" />
        </v-col>
        <v-col cols="6" class="py-1">
          <DisplayField label="Dwelling Use" :value="dwellingUse" />
        </v-col>
        <v-col cols="6" class="py-1">
          <DisplayField label="Total Living Area" :value="homeUnderwriting.totalSquareFeet" type="sqft" />
        </v-col>
        <v-col cols="6" class="py-1">
          <DisplayField label="Purchase Price" :value="homeUnderwriting.purchasePrice" type="currency" />
        </v-col>
        <v-col cols="6" class="py-1">
          <DisplayField label="Purchase Date" :value="homeUnderwriting.purchaseDate" type="date" />
        </v-col>
        <v-col cols="6" class="py-1">
          <DisplayField label="Construction Type" :value="constructionType" />
        </v-col>
        <template v-if="isMobileOrTrailer">
          <v-col cols="6" class="py-1">
            <DisplayField label="Make" :value="homeUnderwriting.mobileMake" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Model" :value="homeUnderwriting.mobileModel" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="In Park" :value="homeUnderwriting.inPark" type="bool" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Park Name" :value="homeUnderwriting.parkName" />
          </v-col>
        </template>
        <template v-if="viewingMore">
          <v-col cols="6" class="py-1">
            <DisplayField label="County" :value="homeUnderwriting.county" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Territory" :value="homeUnderwriting.territory" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Premium Group" :value="homeUnderwriting.premiumGroup" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Protection Class" :value="homeUnderwriting.protectionClass" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Dwelling Location" :value="dwellingLocation" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Dist To Coast" :value="homeUnderwriting.distanceToCoast" type="number" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Garage Area" :value="homeUnderwriting.garageArea" type="sqft" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Basement Area" :value="homeUnderwriting.basementArea" type="sqft" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Roof Type" :value="roofType" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Heat Source" :value="heatSource" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Number of Units" :value="homeUnderwriting.numberOfUnits" type="number" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Stories" :value="stories" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Number of Rooms" :value="homeUnderwriting.numberOfRooms" type="number" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Number of Families" :value="homeUnderwriting.numberOfFamilies" type="number" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Burglar Alarm" :value="homeUnderwriting.burglarAlarm" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Fire Alarm" :value="homeUnderwriting.fireAlarm" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Dead Bolt" :value="homeUnderwriting.hasDeadBolts" type="bool" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Fire Extinguisher" :value="homeUnderwriting.hasFireExtinguisher" type="bool" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Sprinkler" :value="homeUnderwriting.sprinklers" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Feet From Hydrant" :value="homeUnderwriting.feetFromHydrant" type="number" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Wood Stove Inserts" :value="homeUnderwriting.numberOfWoodStoveInserts" type="number" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Hearths" :value="homeUnderwriting.numberOfHearths" type="number" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Chimneys" :value="homeUnderwriting.numberOfChimneys" type="number" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Pre-Fabs" :value="homeUnderwriting.numberOfPreFabs" type="number" />
          </v-col>
        </template>
      </v-row>
      <template v-if="viewingMore">
        <v-divider />
        <v-row class="py-2" wrap>
          <v-col cols="12" class="py-1">
            <DisplayField label="Swimming Pool" :value="swimmingPool" />
          </v-col>
          <v-col cols="12" class="py-1">
            <DisplayField label="Trampoline" :value="trampoline" />
          </v-col>
        </v-row>
        <v-divider />
        <v-row class="py-2" wrap>
          <v-col cols="12" class="section-header pt-0 pb-1">Fire District Information</v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Fire District #" :value="homeUnderwriting.fireDistrictNumber" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Fire District Name" :value="homeUnderwriting.fireDistrictName" />
          </v-col>
          <v-col cols="6" class="py-1">
            <DisplayField label="Fire District Distance" :value="milesFromRuralFireDept" />
          </v-col>
        </v-row>
        <v-divider />
        <v-row class="py-2" wrap>
          <v-col cols="12" class="section-header pt-0 pb-1">Renovations</v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Type" value="Wiring" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Year" :value="homeUnderwriting.wiringImprovementYear" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Code" :value="wiringCode" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Type" value="Plumbing" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Year" :value="homeUnderwriting.plumbingImprovementYear" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Code" :value="plumbingCode" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Type" value="Heating" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Year" :value="homeUnderwriting.heatImprovementYear" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Code" :value="heatingCode" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Type" value="Roof" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Year" :value="homeUnderwriting.roofImprovementYear" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Code" :value="roofCode" />
          </v-col>
        </v-row>
        <v-divider />
        <v-row class="pt-2" wrap>
          <v-col cols="12" class="section-header pt-0 pb-1">Animal Exposures</v-col>
        </v-row>
        <v-row v-for="(animal, a) in homeUnderwriting.animalExposures" :key="a">
          <v-col cols="4" class="py-1">
            <DisplayField label="Type" :value="animalType(animal.type)" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Breed" :value="animalBreed(animal.breed, animal.type)" />
          </v-col>
          <v-col cols="4" class="py-1">
            <DisplayField label="Bite History?" :value="animal.biteHistory" type="bool" />
          </v-col>
        </v-row>
      </template>
      <v-row justify="end">
        <v-col cols="6" class="text-end">
          <a class="link" @click="toggleView">
            {{ viewMoreText }}
          </a>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<style scoped>
.section-header {
    font-style: normal;
    font-weight: bold;
    font-size: 12px;
    line-height: 12px;
    letter-spacing: 0.25px;
    text-transform: uppercase;
    color: #767676;
  }
.renovations-subheader {
    font-style: normal;
    font-size: 12px;
    line-height: 12px;
    letter-spacing: 0.25px;
    text-transform: uppercase;
    color: #767676;
}
</style>
<script>
import apiService from '@/services/shared/agentPortalApiService.js';
import DisplayField from '@/components/Shared/DisplayField.vue';
import enumService from '@/services/shared/enumService.js';
import displayFormatterService from '@/services/shared/displayFormatterService.js';
import analyticsService from '@/services/shared/analyticsService.js';
import { mapActions } from 'vuex';

export default {
  name: 'HomeUnderwriting',
  components: {
    DisplayField
  },
  data: function () {
    return {
      loading: true,
      error: false,
      viewingMore: false,
      detailsLoaded: false,
      homeUnderwriting: {}
    };
  },
  computed: {
    clientId: function () {
      return this.$route.params.clientId;
    },
    policyOrderNumber: function () {
      return this.$route.params.policyOrderNumber;
    },
    residenceType: function () {
      return enumService.dwellingResidenceType[this.homeUnderwriting.dwellingResidenceType];
    },
    occupancyType: function () {
      return enumService.occupancyType[this.homeUnderwriting.occupancyType];
    },
    dwellingUse: function () {
      return enumService.dwellingUseType[this.homeUnderwriting.dwellingUseType];
    },
    dwellingLocation: function () {
      return enumService.dwellingLocation[this.homeUnderwriting.dwellingLocation];
    },
    milesFromRuralFireDept: function () {
      return displayFormatterService.formatNumber(this.homeUnderwriting.milesFromRuralFireDept) + ' Mile(s)';
    },
    roofType: function () {
      return enumService.roofMaterialType[this.homeUnderwriting.roofMaterialType];
    },
    heatSource: function () {
      return enumService.homeHeatSourceType[this.homeUnderwriting.homeHeatSourceType];
    },
    stories: function () {
      return enumService.dwellingStories[this.homeUnderwriting.dwellingStories];
    },
    wiringCode: function () {
      return enumService.improvementStatus[this.homeUnderwriting.wiringImprovementStatus];
    },
    plumbingCode: function () {
      return enumService.improvementStatus[this.homeUnderwriting.plumbingImprovementStatus];
    },
    roofCode: function () {
      return enumService.improvementStatus[this.homeUnderwriting.roofImprovementStatus];
    },
    heatingCode: function () {
      return enumService.improvementStatus[this.homeUnderwriting.heatImprovementStatus];
    },
    swimmingPool: function () {
      return enumService.swimmingPoolExposureType[this.homeUnderwriting.swimmingPoolExposureType];
    },
    trampoline: function () {
      return enumService.trampolineType[this.homeUnderwriting.trampolineType];
    },
    constructionType: function () {
      return enumService.buildingConstructionType[this.homeUnderwriting.constructionType];
    },
    isMobileOrTrailer: function () {
      const mobileTypes = [1, 11];
      return mobileTypes.includes(this.homeUnderwriting.constructionType);
    },
    viewMoreText: function () {
      return this.viewingMore ? 'View Less' : 'View More';
    }
  },
  created: async function () {
    this.loading = true;

    await this.getHomeUnderwritingData(false);

    this.loading = false;
  },
  methods: {
    ...mapActions(['setError']),
    getHomeUnderwritingData: async function (detail) {
      var rs = await apiService.getHomeUnderwriting(this.clientId, this.policyOrderNumber, detail);
      if (rs.code === 0) {
        if (rs.data.length === 0) this.homeUnderwriting = {};
        this.homeUnderwriting = rs.data[0];
      } else {
        this.error = true;
        this.setError(rs.message);
      }
    },
    toggleView: async function () {
      if (this.detailsLoaded === false) {
        await this.getHomeUnderwritingData(true);
        this.detailsLoaded = true;
      }
      if (!this.viewingMore) {
        analyticsService.sendEvent(this.$gtag, 'View More', 'Policy', 'Home Underwriting View More');
      }
      this.viewingMore = !this.viewingMore;
    },
    animalType: function (type) {
      if (type === -1) {
        return '';
      }
      return enumService.animalType[type];
    },
    animalBreed: function (breed, type) {
      if (this.isDog(type)) {
        return enumService.animalBreed[breed];
      }
      return '';
    },
    isDog: function (type) {
      return type === 1;
    }
  }
};
</script>
